// The model and the window's own state, handed to every screen.

import { createContext, useContext, useEffect, useState, useSyncExternalStore } from "react";
import type { HabitPeriodTick } from "../core/habits";
import type { ArchiveFilter, Route } from "../core/lists";
import type { Day } from "../core/types";
import { key } from "../i18n";
import type { MailKind } from "../data/mail";
import type { IconName } from "./Icon";
import type { Model, ModelState } from "./model";
import type { WeekView } from "./Week";

export function useModelState(model: Model): ModelState {
  return useSyncExternalStore(model.subscribe, model.getState);
}

/** A sheet over the window. */
export type Sheet =
  | { kind: "pro"; feature: ProFeature }
  | { kind: "keys" }
  /** `section`: scrolled to that part (Habits' ··· › Habits Settings…). */
  | { kind: "settings"; section?: "habits" }
  | { kind: "assistant" }
  | { kind: "newProject" }
  | { kind: "useTemplate"; templateID: string }
  /** A break for a habit, until a day. */
  | { kind: "pauseHabit"; habitID: string }
  /** A streak's milestone (7, 30, 100, 365 days …), once, right after the tick. */
  | { kind: "milestone"; habitID: string; count: number }
  /** Who is in a shared list, project or area. */
  | { kind: "people"; route: Route }
  | { kind: "join"; invitationID: string }
  /** The Email to Todolix address of the Inbox, an area or a project. */
  | { kind: "mail"; mailKind: MailKind; targetID?: string | undefined; label: string }
  /**
   * `typed`: the action waits until DELETE is typed, as the apps ask before deleting data or the account.
   * `quiet`: an action that takes nothing away (Count It), in mint rather than red. `onCancel`: the other answer (the
   * cancel button, Esc or a click outside), as "Today" for a tick the morning after (docs/70).
   */
  | { kind: "confirm"; title: string; text: string; action: string; run: () => void | Promise<void>; typed?: boolean; quiet?: boolean; cancel?: string; onCancel?: () => void };

export type ProFeature = "assistant" | "favorites" | "homeLists" | "history" | "mail" | "templates" | "habits";

export const PRO_FEATURES: Record<ProFeature, { icon: IconName; title: string; text: string }> = {
  assistant: { icon: "sparkle", title: key("The Assistant"), text: key("Ask about your day, or say what to change.") },
  favorites: { icon: "star", title: key("Favorites"), text: key("Pin the projects and lists you use most to the top of Home.") },
  homeLists: { icon: "lines", title: key("Home Your Way"), text: key("Choose which lists Home shows, so it holds just what you use.") },
  history: { icon: "archive", title: key("Your Whole History"), text: key("Everything you have done, beyond the last 30 days. It is all kept; Pro shows it.") },
  templates: { icon: "templates", title: key("Templates"), text: key("What you do now and then, written down once: as many templates as you need. Two are free.") },
  habits: { icon: "flame", title: key("Habits"), text: key("What you mean to do again and again, with its streak: as many habits as you need. Three are free.") },
  mail: { icon: "mail", title: key("Email to Todolix"), text: key("Send or forward mail to your own addresses, and it arrives as a to-do in the Inbox, an area or a project.") },
};

/** Where Quick Entry adds the new to-do within the list. */
export interface Composing {
  sectionID?: string;
  after?: string;
  /** A column of the week: the new to-do starts that day (docs/56). */
  day?: Day;
}

export interface Toast {
  id: number;
  text: string;
  undo?: boolean;
  /** A tick of a flexible habit's day: the toast tells its week (boards 9 and 10, Bastian 2026-10-31). */
  habit?: HabitWeekToast;
}

/** What the toast says after a tick of a flexible habit's day (the kit's HabitWeekNote). */
export interface HabitWeekToast {
  title: string;
  tick: HabitPeriodTick;
  /** The streak said once it closes; undefined with Show Streaks off or under 2. */
  streak?: number;
}

export interface UI {
  route: Route;
  /** Narrow: Home or the list fills the window. */
  screen: "home" | "list";
  selected: readonly string[];
  open: string | null;
  composing: Composing | null;
  finding: boolean;
  query: string;
  sheet: Sheet | null;
  toast: Toast | null;
  sidebarOpen: boolean;
  sidebarHidden: boolean;
  /** The Archive narrowed to one project or area; null for everything. Forgotten when the list is left. */
  archiveFilter: ArchiveFilter | null;
  /** The first day the week shows; null is today (‹ › page by a day, « » by a week, docs/56). */
  weekStart: Day | null;
  /** The filter of "Unscheduled", and the columns unfolded past their first eight rows. */
  weekFilter: string;
  weekExpanded: readonly string[];
  /** A habit's page (docs/63): its open card grown into the content area; null for the list. */
  habitPage: string | null;
  /** Habits: Today's or all of them. Forgotten when the list is left. */
  habitsFilter: "today" | "all";
}

export interface Window {
  model: Model;
  state: ModelState;
  ui: UI;
  /** Changes the window's state. */
  set(change: Partial<UI> | ((ui: UI) => Partial<UI>)): void;
  go(route: Route): void;
  select(id: string, how?: { toggle?: boolean; range?: boolean }): void;
  openTodo(id: string | null): void;
  compose(composing?: Composing): void;
  showToast(text: string, undo?: boolean, habit?: HabitWeekToast): void;
  showSheet(sheet: Sheet | null): void;
  /** The to-dos in the list as they show, top to bottom (collapsed sections left out). */
  visibleIDs: readonly string[];
  layout: Layout;
  /** Scheduled as a week, when it shows (docs/56). */
  week: WeekView | null;
}

export interface Layout {
  narrow: boolean;
  medium: boolean;
  /** A finger rather than a mouse: a tap opens. */
  touch: boolean;
}

export const WindowContext = createContext<Window | null>(null);

export function useWindow(): Window {
  const window = useContext(WindowContext);
  if (!window) throw new Error("useWindow outside the window");
  return window;
}

export function useMedia(query: string): boolean {
  const [matches, setMatches] = useState(() => typeof matchMedia === "function" && matchMedia(query).matches);
  useEffect(() => {
    const list = matchMedia(query);
    const update = () => setMatches(list.matches);
    update();
    list.addEventListener("change", update);
    return () => list.removeEventListener("change", update);
  }, [query]);
  return matches;
}
